// src/layouts/trafficmonitoring/routeFlows/index.js

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import RouteFlowBarDiagram from "./RouteFlowBarDiagram";
import RouteFlowsFilter from "./RouteFlowsFilter";

// Default (Last 24h) schema + orchestrator
import routeFlowsSchema from "./schema";
import { groupRouteFlows } from "./groupRouteFlows";

// Historical (by date) schema + API (expects full in_timestamp)
import routeFlowsDateSchema from "./schemaDate";
import { fetchAllRouteFlowsForDate } from "./routeFlowsApiDate";

import {
  sanitizeCharts,
  computeGlobalMaxY,
  enrichChartOptions,
} from "./functions";

// Register Chart.js modules once
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const spaceGraphs = 200;

function RouteFlows() {
  const { t } = useTranslation();
  // UI state
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  // filter state
  const [selectedDate, setSelectedDate] = useState(null); // Date | string | null
  const activeModeRef = useRef("last24h"); // "last24h" | "date"

  // Keep an AbortController per fetch to avoid race conditions
  const abortRef = useRef(null);

  // Central loader: accepts full in_timestamp (string) or null (last 24h)
  const loadCharts = async (inTimestampOrNull) => {
    // cancel previous in-flight request
    try {
      abortRef.current?.abort();
    } catch (_) {}
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      setLoading(true);
      setPageError(null);

      let raw;

      if (inTimestampOrNull) {
        // HISTORICAL mode → use get_route_flows_* with in_timestamp
        console.log("[RouteFlows] HISTORICAL mode. in_timestamp:", inTimestampOrNull);
        raw = await fetchAllRouteFlowsForDate(routeFlowsDateSchema, {
          inTimestamp: inTimestampOrNull, // full ISO "YYYY-MM-DDTHH:mm:ssZ"
          signal: ac.signal,
        });
      } else {
        // DEFAULT (Last 24h) → use latest_*_transformed/_with_typical
        raw = await groupRouteFlows(routeFlowsSchema, {
          date: null,
          signal: ac.signal,
        });
      }

      const filtered = sanitizeCharts(raw);
      const globalMax = computeGlobalMaxY(filtered);

      const updated = filtered.map((chart) => ({
        ...chart,
        options: enrichChartOptions({
          baseOptions: chart.options,
          ChartJS,
          showLegend: true,
          datalabelsConfig: {
            anchor: "end",
            align: "end",
            offset: -4,
            fontSize: 12,
            color: "#888a91",
          },
          yMax: globalMax + spaceGraphs,
        }),
      }));

      setCharts(updated);
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error(err);
        setPageError(t("trafficMonitoring.routeFlows.loadError"));
      }
    } finally {
      setLoading(false);
    }
  };

  // initial load: Last 24h
  useEffect(() => {
    activeModeRef.current = "last24h";
    loadCharts(null);
    return () => {
      try {
        abortRef.current?.abort();
      } catch (_) {}
    };
  }, []);

  // Render content (charts area)
  const chartsContent = useMemo(() => {
    if (loading) {
      return (
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6">{t("trafficMonitoring.routeFlows.loading")}</MDTypography>
        </MDBox>
      );
    }
    if (pageError) {
      return (
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6">{pageError}</MDTypography>
        </MDBox>
      );
    }
    if (!charts?.length) {
      return (
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6">{t("trafficMonitoring.routeFlows.noDataAvailable")}</MDTypography>
        </MDBox>
      );
    }
    return charts.map((chart, i) => (
      <RouteFlowBarDiagram
        key={chart.id || i}
        data={chart.data}
        options={chart.options}
        plugins={[ChartDataLabels]}
        height={350}
      />
    ));
  }, [charts, loading, pageError, t]);

  return (
    <DashboardLayout>
      <MDBox p={2}>
        <RouteFlowsFilter
          selectedDate={selectedDate}
          onSelectedDateChange={(d) => setSelectedDate(d)}
          onApply={(inTimestamp) => {
            activeModeRef.current = "date";
            loadCharts(inTimestamp); // switch → HISTORICAL mode with full timestamp
          }}
          onLast24h={() => {
            activeModeRef.current = "last24h";
            setSelectedDate(null);
            loadCharts(null); // switch → DEFAULT mode
          }}
        />
      </MDBox>

      {chartsContent}
    </DashboardLayout>
  );
}

export default RouteFlows;
